import Loader from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import classNames from 'classnames'
import {differenceInSeconds, parseISO} from 'date-fns'
import {Suspense} from 'react'

import useTimeSelector from '../../../hooks/useTimeSelector'
import {formatDuration} from '../../../utils/dateTimeUtils'
import SvgIcon from '../SvgIcon/SvgIcon'

import type {Props} from './BuildQueueInfo.types'
import WaitReasonsList from './WaitReasonsList/WaitReasonsList'

import styles from './BuildQueueInfo.css'

function BuildQueueInfo({
  className,
  buildId,
  inline,
  queuedDate,
  startDate,
  currentWaitReason,
  aside,
  queued,
  titleClassName,
  reasonsClassName,
  dependenciesRange,
  highlightText = true,
  withoutTitle,
  groupOwnReasons,
  withIcons,
}: Props) {
  const startDateObj = startDate != null ? parseISO(startDate) : null
  const startOrNow = useTimeSelector(now => Number(startDateObj ?? now))

  if (queuedDate == null) {
    return null
  }

  const timeInQueue = differenceInSeconds(startOrNow, parseISO(queuedDate))

  const titleClasses = classNames(titleClassName, styles.title, {
    [styles.inlineTitle]: inline === true && highlightText === true,
  })
  const title = (
    <span className={titleClasses} data-test-queue-info="true">
      {withIcons && (
        <span className={styles.iconsOffset}>
          <SvgIcon icon="hourglass" className={styles.queuedIcon} />
        </span>
      )}
      {!withoutTitle && 'Queue time '}
      {formatDuration(timeInQueue)}
    </span>
  )

  const reasonsList = (
    <WaitReasonsList
      buildId={buildId}
      reasonsClassName={reasonsClassName}
      groupOwnReasons={groupOwnReasons}
      timeInQueue={timeInQueue}
      queued={queued}
      currentWaitReason={currentWaitReason}
      withIcons={withIcons}
      highlightText={highlightText}
      dependenciesRange={dependenciesRange}
      inline={inline}
      title={title}
    />
  )

  if (!inline) {
    return (
      <div className={classNames(className, styles.wrapper)}>
        <Suspense fallback={title}>{reasonsList}</Suspense>
      </div>
    )
  }

  return (
    <div className={classNames(className, styles.wrapper)}>
      {title}
      {inline && aside}
      <div className={classNames(reasonsClassName, styles.inlineReasons)}>
        <Suspense fallback={<Loader>{'Loading reasons...'}</Loader>}>{reasonsList}</Suspense>
      </div>
    </div>
  )
}

export default BuildQueueInfo
